"use client";

import React from "react";
import { ShieldCheck, Zap, Layers, ArrowRight, FileCheck, CircleCheck as CheckCircle2, Sparkles, SearchCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
export default function RamanHero() {
  const shouldReduceMotion = useReducedMotion();
  const keyBenefits = ["Non-destructive raw material identification", "Audit trail and data integrity controls", "Through-barrier testing for bags, bottles, and vials", "Fast pass/fail verification, timing dependent on instrument and application"];
  const specs = [{
    label: "Laser Wavelength",
    value: "1064 nm / 785 nm",
    description: "Optimized fluorescence suppression"
  }, {
    label: "Spectral Range",
    value: "175 – 3,200 cm⁻¹",
    description: "Broad pharmaceutical compound coverage"
  }, {
    label: "Battery Run Time",
    value: "8+ Hours",
    description: "Continuous warehouse testing"
  }, {
    label: "Compliance",
    value: "USP <1120> & EP 2.2.48",
    description: "Full regulatory qualification support"
  }];
  return <section id="productfamilyhero" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 border-b border-border">
      {/* Background Subtle Gradient Accents */}
      <div className="pointer-events-none absolute -top-40 right-0 h-96 w-96 rounded-full bg-primary/5 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-40 -left-20 h-96 w-96 rounded-full bg-secondary/80 blur-3xl" aria-hidden="true" />

      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          {/* Left Column: Product Information & Value Proposition */}
          <div className="lg:col-span-7 flex flex-col justify-center">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                <Sparkles className="size-3.5 text-primary mr-1" />
                Handheld Raman Family
              </Badge>
              <Badge variant="outline" className="px-3 py-1 text-xs font-medium text-muted-foreground">
                Pharma & Security Grade
              </Badge>
            </div>

            <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:leading-tight">
              Rapid, Non-Destructive Raw Material ID & Chemical Verification
            </h1>

            <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              Antech Solutions provides handheld Raman analyzers engineered for regulated pharmaceutical receiving docks, chemical manufacturing, and field defense units across Ireland and the UK.
            </p>

            {/* Core Feature Bullet Points */}
            <div className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {keyBenefits.map((benefit, i) => <div key={benefit} data-index={i} className="flex items-center gap-2 text-sm text-foreground">
                  <CheckCircle2 className="size-4 shrink-0 text-primary" />
                  <span>{benefit}</span>
                </div>)}
            </div>

            {/* CTAs */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="h-11 px-6 font-medium shadow-sm">
                <Link to="/ContactPage">
                  Request a Demo
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-11 px-6 font-medium">
                <Link to="/ProductsPage">
                  Compare Models
                </Link>
              </Button>
            </div>

            {/* Regulatory Support Footnote */}
            <div className="mt-8 pt-6 border-t border-border flex flex-wrap items-center gap-6 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <ShieldCheck className="size-4 text-primary" />
                <span>Full IQ/OQ/PQ Qualification Kits</span>
              </div>
              <div className="flex items-center gap-2">
                <FileCheck className="size-4 text-primary" />
                <span>Direct Method Transfer Support</span>
              </div>
            </div>
          </div>

          {/* Right Column: Visual Showcase & Technical Stats */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 20
          }} animate={shouldReduceMotion ? undefined : {
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="relative">
              <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-3 shadow-md">
                <div className="relative aspect-4/3 w-full overflow-hidden rounded-xl bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/raman_handheld_analyzer_device.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/raman_handheld_analyzer_device.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/raman_handheld_analyzer_device.png-wvc-sizes" alt="Antech Solutions handheld Raman spectrometer analyzer for pharmaceutical raw material testing" loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-[transform] duration-500 hover:scale-105" />
                  <div className="absolute top-3 right-3">
                    <Badge variant="default" className="bg-primary text-primary-foreground shadow-xs">
                      1064 nm Optics
                    </Badge>
                  </div>
                </div>

                <div className="mt-3 px-2 py-1 flex items-center justify-between text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">Pro-Series Handheld Platform</span>
                  <span>IP67 Rugged Enclosure</span>
                </div>
              </div>
            </motion.div>

            {/* Quick Specs Grid */}
            <div className="grid grid-cols-2 gap-3">
              {specs.map((item, i) => <Card key={item.label} data-index={i} className="py-3.5 px-4 rounded-xl border border-border shadow-xs bg-card">
                  <CardContent className="p-0">
                    <span className="block text-xs font-medium text-muted-foreground">
                      {item.label}
                    </span>
                    <span className="mt-1 block text-sm font-semibold text-foreground">
                      {item.value}
                    </span>
                    <span className="mt-0.5 block text-[11px] text-muted-foreground line-clamp-1">
                      {item.description}
                    </span>
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </div>

        {/* Application Quick Access Badges Bar */}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <SearchCheck className="size-4 text-primary" />
              <span>Target Analytical Applications</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button asChild variant="secondary" size="sm" className="h-7 text-xs font-normal">
                <Link to="/RawMaterialIdentificationPage">
                  Raw Material ID
                </Link>
              </Button>
              <Button asChild variant="secondary" size="sm" className="h-7 text-xs font-normal">
                <Link to="/SafetyAndSecurityPage">
                  Safety & Security
                </Link>
              </Button>
              <Button asChild variant="secondary" size="sm" className="h-7 text-xs font-normal">
                <Link to="/PharmaceuticalManufacturingPage">
                  Pharma Manufacturing
                </Link>
              </Button>
              <Button asChild variant="secondary" size="sm" className="h-7 text-xs font-normal">
                <Link to="/ProcessAnalyticalTechnologyPage">
                  PAT Integration
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}